import { useCallback, useEffect, useRef, useState } from 'react';
import { getPublishJob, publishRequest } from '../generation/generationApi';
import { isPlanError } from '../../components/plan/planErrors';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { PublishJobRead, PublishJobStatus } from '../../types/api';

/** How often the job is re-read while the worker is still on it. */
const POLL_INTERVAL_MS = 2000;

/** Polls before giving up — five minutes at the interval above. */
const POLL_LIMIT = 150;

/** The statuses that mean the worker has not finished with this job. */
const RUNNING: readonly PublishJobStatus[] = ['pending', 'uploading', 'posting'];

function isRunning(job: PublishJobRead | null): boolean {
  return job !== null && RUNNING.includes(job.status);
}

export interface PublishJobFlow {
  /** The job this screen is watching. Null until one is started or adopted. */
  job: PublishJobRead | null;
  /** Failure of the *request to publish*, not of the job — that is `job.error_detail`. */
  error: string | null;
  /** A 402 the publish request answered with, for `UpsellFromError`; `error` stays null then. */
  planError: ApiError | null;
  /** A publish is being asked for, or one is running. */
  isBusy: boolean;
  start: () => void;
}

/** Result of the last load, tagged with the request it belongs to. */
interface TrackedJob {
  key: string;
  job: PublishJobRead | null;
}

/** Message for the guards the publish endpoint answers with. */
function publishMessage(caught: unknown, label: string): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'INTEGRATION_DISABLED':
        return `${label} publishing is not configured on this server.`;
      case 'REQUEST_NOT_COMPLETE':
        return 'This post is not finished, so there is nothing to publish.';
      case 'PLATFORM_MISMATCH':
        return 'This post was written for another platform.';
      case 'LINKEDIN_NOT_CONNECTED':
        return 'No LinkedIn account is connected. Connect one from Connected accounts.';
      case 'LINKEDIN_RECONNECT_REQUIRED':
        return 'The LinkedIn connection expired. Reconnect, then publish again.';
      case 'FACEBOOK_NOT_CONNECTED':
        return 'No Facebook Page is connected. Connect one from Connected accounts.';
      case 'FACEBOOK_RECONNECT_REQUIRED':
        return 'The Facebook connection stopped working. Reconnect, then publish again.';
      case 'META_UNAVAILABLE':
        return 'Facebook did not answer. Try again in a minute.';
      case 'META_REJECTED':
        return 'Facebook refused this post.';
      case 'EXPORT_NOT_READY':
        return 'Still rendering — try again shortly.';
      default:
        break;
    }
  }
  return getErrorMessage(caught, `That did not reach ${label}. Try again.`);
}

/**
 * Publishing one finished request, as a small state machine: idle, publishing,
 * then complete or failed.
 *
 * The API only enqueues — the actual upload-and-post runs in an Arq worker, so
 * progress is read by polling the job rather than awaited. A `setTimeout` chain
 * rather than an interval, so a slow response cannot stack requests, and the
 * whole chain is torn down on unmount or a change of request. A dropped poll
 * says nothing about the post: it spends a poll and carries on.
 *
 * A job already running is adopted on mount, so a refresh mid-publish rejoins
 * the one that is in flight instead of offering to start a second. The same
 * goes for 409 `PUBLISH_IN_PROGRESS`, which is not a failure but an answer:
 * this request is already being published, here is the job.
 *
 * `ALREADY_PUBLISHED` is treated as success, exactly as the manual record does
 * — the request is in the state the caller asked for, the detail already
 * carries `published_post`, so it refreshes rather than complaining.
 *
 * `onPublished` fires when a post really went out; the caller re-reads the
 * request, which is where the permalink and the Posted stamp come from.
 *
 * `label` names the platform in error copy (`LinkedIn`, `Facebook`).
 */
export function usePublishJob(
  businessId: string,
  requestId: string,
  onPublished: () => void,
  label: string,
): PublishJobFlow {
  const [tracked, setTracked] = useState<TrackedJob | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planError, setPlanError] = useState<ApiError | null>(null);

  // Held in a ref so a caller passing a fresh closure each render cannot
  // restart the poll chain mid-publish.
  const publishedRef = useRef(onPublished);
  useEffect(() => {
    publishedRef.current = onPublished;
  }, [onPublished]);

  const key = `${businessId}:${requestId}`;
  const job = tracked !== null && tracked.key === key ? tracked.job : null;
  const running = isRunning(job);

  // Adopt whatever is already in flight for this request. Only a running job:
  // a finished one is already visible as the published post, and an old failure
  // is not news on arrival.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const existing = await getPublishJob(businessId, requestId, controller.signal);
        if (cancelled || !isRunning(existing)) return;
        setTracked((current) =>
          current !== null && current.key === key ? current : { key, job: existing },
        );
      } catch {
        // Nothing to say: not knowing about an old job costs the user nothing,
        // and starting one would report its own failure.
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, requestId, key]);

  useEffect(() => {
    if (!running) return;

    const controller = new AbortController();
    let cancelled = false;
    let timer: number | undefined;
    let polls = 0;

    function schedule() {
      timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
    }

    async function poll() {
      if (cancelled) return;
      polls += 1;

      try {
        const next = await getPublishJob(businessId, requestId, controller.signal);
        if (cancelled) return;
        if (next !== null) {
          setTracked({ key, job: next });
          if (next.status === 'complete') {
            // The published post, the permalink and the stamp all live on the
            // request, so the screen re-reads it rather than inventing them.
            publishedRef.current();
            return;
          }
          if (next.status === 'failed') return;
        }
      } catch {
        // A dropped poll says nothing about the post. Spend a poll, not the job.
        if (cancelled || controller.signal.aborted) return;
      }

      if (polls >= POLL_LIMIT) {
        setError('This is taking longer than usual. Reload to check whether it went out.');
        return;
      }
      schedule();
    }

    schedule();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [businessId, requestId, key, running]);

  const start = useCallback(() => {
    if (isStarting || running) return;

    setError(null);
    setPlanError(null);
    setIsStarting(true);

    void (async () => {
      try {
        const created = await publishRequest(businessId, requestId);
        setTracked({ key, job: created });
        setIsStarting(false);
      } catch (caught) {
        setIsStarting(false);

        if (caught instanceof ApiError && caught.code === 'ALREADY_PUBLISHED') {
          publishedRef.current();
          return;
        }

        if (caught instanceof ApiError && caught.code === 'PUBLISH_IN_PROGRESS') {
          // Not a failure: someone already asked. Join that job instead.
          try {
            const existing = await getPublishJob(businessId, requestId);
            if (existing !== null) {
              setTracked({ key, job: existing });
              return;
            }
          } catch {
            // Fall through to the plain message below.
          }
          setError('This post is already being published.');
          return;
        }

        if (isPlanError(caught)) {
          setPlanError(caught);
          return;
        }

        setError(publishMessage(caught, label));
      }
    })();
  }, [businessId, isStarting, key, label, requestId, running]);

  return { job, error, planError, isBusy: isStarting || running, start };
}
